import { $, rect } from "@utils/dom";
import Drag from "@utils/drag";
import { on, off } from "@utils/listener";
import { lerp, limit } from "@utils/math";
import RAF from "@utils/raf";
import ResizeOrientation from "@utils/resize";

const LABEL_INVERTED = '--label-inverted';

class PbRowComparisonMedias {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.slider = $('.pb-row-comparison-medias__slider', this.el);
    this.rightMedia = $('.pb-row-comparison-medias__rightMedia', this.el);
    this.separator = $('.pb-row-comparison-medias__separator', this.el);
    this.label = $('.pb-row-comparison-medias__labelWrap', this.el);

    this._data = { px: null, py: 50, width: null, height: null, active: false, first: true, labelInverted: false, supportMouse: false };
    this._drag = null;
    this._raf = null;

    this._onResize = this._onResize.bind(this);
    this._onMouseMove = this._onMouseMove.bind(this);
    this._onDragStart = this._onDragStart.bind(this);
    this._onDragMove = this._onDragMove.bind(this);
    this._onDragEnd = this._onDragEnd.bind(this);
    this._onRAF = this._onRAF.bind(this);
  }

  init() {
    this._data.px = parseFloat( getComputedStyle(this.slider).getPropertyValue('--start-value') );
    this._data.labelInverted = this.label.classList.contains(LABEL_INVERTED);
    this._drag = new Drag(this.slider, { preventScrolling: true });
    this._raf = RAF.add(this._onRAF);

    this._onResize();
    this._bindEvents();
  }
  destroy() {
    this._unbindEvents();

    if( this._drag ) this._drag.destroy();
    if( this._raf ) RAF.remove(this._onRAF);

    this.el = null;
    this.emitter = null;
    this.slider = null;
    this.rightMedia = null;
    this.separator = null;
    this.label = null;

    this._data = null;
    this._drag = null;
    this._raf = null;

    this._onResize = null;
    this._onMouseMove = null;
    this._onDragStart = null;
    this._onDragMove = null;
    this._onDragEnd = null;
    this._onRAF = null;
  }

  _bindEvents() {
    ResizeOrientation.add(this._onResize);

    on(this.slider, 'mousemove', this._onMouseMove);

    this._drag.on('start', this._onDragStart);
    this._drag.on('move', this._onDragMove);
    this._drag.on('end', this._onDragEnd);
    this._drag.start();
  }
  _unbindEvents() {
    this._drag.stop();
    this._drag.off('start', this._onDragStart);
    this._drag.off('move', this._onDragMove);
    this._drag.off('end', this._onDragEnd);

    off(this.slider, 'mousemove', this._onMouseMove);

    ResizeOrientation.remove(this._onResize);
  }

  _onResize() {
    const { width, height } = rect(this.slider);

    this._data.width = width;
    this._data.height = height;
  }
  _onMouseMove(event) {
    if( this._data.first ) {
      this._data.first = false;

      this.rightMedia.style.transition = 'none';
      this.separator.style.transition = 'none';
      this.label.style.transition = 'none';
    }

    this._data.supportMouse = true;
    this._data.event = event;
    this._data.active = true;

    this._raf(true);
  }
  _onDragStart(event) {
    // stop here if mouseMove is supported
    if( this._data.supportMouse ) return;

    if( this._data.first ) {
      this._data.first = false;

      this.rightMedia.style.transition = 'none';
      this.separator.style.transition = 'none';
      this.label.style.transition = 'none';
    }

    this._data.event = event;
    this._data.active = true;
    this._raf(true);
  }
  _onDragMove(event) {
    if( !this._data.supportMouse ) this._data.event = event;
  }
  _onDragEnd(event) {
    this._data.event = event;
    this._data.active = false;
    this._data.supportMouse = false;
  }
  _onRAF(delta) {
    const { event, active, width, height, labelInverted } = this._data;

    // calculate targets
    const tx = limit(0, width, event.layerX) / width * 100;
    const ty = limit(0, height, event.layerY) / height * 100;

    // lerp
    let px = lerp(this._data.px, tx, 0.15 * delta);
    let py = lerp(this._data.py, ty, 0.15 * delta);

    // if pointer is up AND percentage has reached target, snap to target and stop RAF
    if( !active ) {
      if( Math.abs(px - tx) < 0.1 && Math.abs(py - ty) < 0.1 ) {
        px = tx;
        py = ty;

        this._raf(false);
      }
    }

    // save current values
    this._data.px = px;
    this._data.py = py;

    // invert label position
    if( px > 50 ) {
      if( !labelInverted ) {
        this._data.labelInverted = true;
        this.label.classList.add(LABEL_INVERTED);
      }
    } else {
      if( labelInverted ) {
        this._data.labelInverted = false;
        this.label.classList.remove(LABEL_INVERTED);
      }
    }

    // update DOM
    this.rightMedia.style.clipPath = `inset(0 0 0 ${px}%)`;
    this.separator.style.transform = `translate3d(${px}%, 0, 0)`;
    this.label.style.transform = `translate3d(${px}%, ${py}%, 0)`;
  }
}

export default PbRowComparisonMedias;
